/**
 * ProductComparisonPanel Component
 * Side-by-side CARA/SAP product comparison for match validation on the StartBRR page.
 */

import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, AlertCircle, ChevronDown, ChevronRight, Shield } from 'lucide-react';
import type { EnrichedProduct, SAPMaterial } from '../services/types';

export interface ProductComparisonPanelProps {
  caraProduct: EnrichedProduct | null;
  sapMaterial: SAPMaterial | null;
  matchType: 'exact' | 'confirmed' | 'candidates' | null;
  isLoading: boolean;
}

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="py-1.5 min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900" style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>{value || '—'}</div>
    </div>
  );
}

function ComparisonRow({
  caraLabel, caraValue, sapLabel, sapValue,
}: {
  caraLabel: string; caraValue: string | null | undefined;
  sapLabel: string; sapValue: string | null | undefined;
}) {
  return (
    <div className="grid grid-cols-2 gap-6 overflow-hidden">
      <FieldRow label={caraLabel} value={caraValue} />
      <FieldRow label={sapLabel} value={sapValue} />
    </div>
  );
}

function formatConfirmedDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function confidenceLabel(score: number | null | undefined): 'High' | 'Medium' | 'Low' | null {
  if (score == null) return null;
  if (score >= 0.8) return 'High';
  if (score >= 0.5) return 'Medium';
  return 'Low';
}

function ConfidenceBadge({ confidence }: { confidence: number | null | undefined }) {
  const label = confidenceLabel(confidence);
  if (!label) return null;
  const colors = label === 'High' ? 'bg-green-100 text-green-700'
    : label === 'Medium' ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors}`}>
      <span>{label} Confidence</span>
    </div>
  );
}

function MatchBadge({
  matchType,
  confidence,
  product,
}: {
  matchType: string | null;
  confidence: number | null;
  product: EnrichedProduct | null;
}) {
  const isUserSelected = product?.userSelected === true;
  const confirmedBy = product?.confirmedBy;
  const confirmedDate = product?.confirmedDate;

  // User explicitly selected this product
  if (isUserSelected && confirmedBy) {
    const detail = ` by ${confirmedBy}${confirmedDate ? ` on ${formatConfirmedDate(confirmedDate)}` : ''}`;
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle size={14} />
          <span>Confirmed{detail}</span>
        </div>
        <ConfidenceBadge confidence={confidence} />
      </div>
    );
  }
  // AI match — exact, embedding, or auto-confirmed without a user audit trail
  if (matchType === 'exact' || matchType === 'confirmed' || matchType === 'candidates') {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          <Shield size={14} />
          <span>AI Match</span>
        </div>
        <ConfidenceBadge confidence={confidence} />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
      <AlertCircle size={14} />
      <span>No Match</span>
    </div>
  );
}

function formatMarkets(markets: EnrichedProduct['markets']): string {
  if (!markets || markets.length === 0) return '—';
  return markets
    .map(m => m.name || m.isoCode || '')
    .filter(Boolean)
    .join(', ');
}

function formatStorage(shelfLives: EnrichedProduct['shelfLives']): string {
  if (!shelfLives || shelfLives.length === 0) return '—';
  return shelfLives
    .map(s => s.storageConditions)
    .filter(Boolean)
    .join('; ');
}

export function ProductComparisonPanel({
  caraProduct,
  sapMaterial,
  matchType,
  isLoading,
}: ProductComparisonPanelProps) {
  const [expanded, setExpanded] = useState(matchType !== 'exact');

  // Update expanded state when matchType changes
  React.useEffect(() => {
    setExpanded(matchType !== 'exact');
  }, [matchType]);

  if (isLoading) {
    return (
      <div className="mt-4 bg-white rounded-lg border-2 border-blue-200 shadow-sm p-4">
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-4 bg-gray-200 rounded w-40"></div>
          <div className="h-5 bg-gray-200 rounded-full w-28"></div>
        </div>
      </div>
    );
  }

  if (!caraProduct && !sapMaterial) {
    return null;
  }

  return (
    <div className="mt-4 bg-white rounded-lg border-2 border-blue-200 shadow-sm" data-testid="product-comparison-panel">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-blue-600" />
          <span className="font-semibold text-gray-900 text-sm">Match Verification</span>
          <MatchBadge matchType={matchType} confidence={caraProduct?.matchConfidence ?? null} product={caraProduct} />
        </div>
        <div className="text-gray-400">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>
      </button>

      {/* Comparison body */}
      {expanded && (
        <div className="border-t border-gray-200 p-4">
          {/* Column headers */}
          <div className="grid grid-cols-2 gap-6 mb-2">
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
              CARA (Product Database)
            </div>
            <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">
              SAP (Material)
            </div>
          </div>

          {/* Paired rows — each row spans both columns so they stay aligned */}
          <div className="divide-y divide-gray-100">
            <ComparisonRow caraLabel="Product Name" caraValue={caraProduct?.name} sapLabel="Product Name" sapValue={sapMaterial?.productName} />
            <ComparisonRow caraLabel="Pharmaceutical Product" caraValue={caraProduct?.pharmaceuticalProduct} sapLabel="Name on Packaging" sapValue={sapMaterial?.nameOnPackaging} />
            <ComparisonRow caraLabel="Medicinal Product" caraValue={caraProduct?.medicinalProduct} sapLabel="Destination Country" sapValue={sapMaterial?.destinationCountry} />
            <ComparisonRow caraLabel="Dosage Form" caraValue={caraProduct?.doseForm} sapLabel="Dosage Form" sapValue={sapMaterial?.dosageForm} />
            <ComparisonRow caraLabel="Strength" caraValue={caraProduct?.doseStrength} sapLabel="Strength" sapValue={sapMaterial?.strength} />
            <ComparisonRow caraLabel="Pack Size" caraValue={caraProduct?.packQuantity} sapLabel="Pack Size" sapValue={sapMaterial?.packageSize} />
            <ComparisonRow caraLabel="Markets" caraValue={caraProduct ? formatMarkets(caraProduct.markets) : null} sapLabel="Countries" sapValue={sapMaterial?.countryCodes} />
            <ComparisonRow caraLabel="Storage" caraValue={caraProduct ? formatStorage(caraProduct.shelfLives) : null} sapLabel="Temperature" sapValue={sapMaterial?.temperatureConditions} />
          </div>

        </div>
      )}
    </div>
  );
}
